import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'sonner';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useAPI } from 'hooks';
import Modal from 'components/shared/Modal';
import CategoryBadge from 'components/shared/CategoryBadge';
import { getUserName } from 'helpers';
import Locales from 'locales';
import { cn } from 'lib/utils';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from 'components/ui/table';

const CreateGroupTripModal = ({
  isOpened = false,
  closeModal = () => {},
  selectedPilgrimIds = null,
  selectedPilgrimsData = null,
}) => {
  const { api } = useAPI();
  const queryClient = useQueryClient();
  const [groupeName, setGroupeName] = useState('');
  const [groupeNameError, setGroupeNameError] = useState(false);
  const [page, setPage] = useState(1);
  const itemsPerPage = 5;

  const indexOfLastItem = page * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = selectedPilgrimsData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(selectedPilgrimsData.length / itemsPerPage);

  const { mutate: createGroupPilgrims, isLoading } = useMutation({
    mutationFn: () => api.post('trip/group_pilgrims/', { group_name: groupeName, member_trip_ids: selectedPilgrimIds }),
    onSuccess: ({ data }) => {
      queryClient.invalidateQueries({ queryKey: ['trip/'] });
      queryClient.invalidateQueries({ queryKey: ['trip/stats/default/'] });
      queryClient.invalidateQueries({ queryKey: ['trip/groupes/'] });
      toast.success(data.success);
      closeModal();
    },
  });

  const handleCreateGroup = () => {
    if (groupeName.trim().length > 0) {
      createGroupPilgrims();
      setGroupeNameError(false);
    } else {
      setGroupeNameError(true);
    }
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) setPage(page);
  };

  return (
    <Modal
      isOpen={isOpened}
      onOpenChange={closeModal}
      title="Créer un groupement"
      description="Choisissez un nom pour votre groupement"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={closeModal}>
            {Locales.base.button.cancel}
          </Button>

          <Button variant="hajj" onClick={handleCreateGroup} disabled={isLoading}>
            {Locales.base.button.save}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <Label htmlFor="group_name">Nom du groupe*</Label>
        <Input
          id="group_name"
          placeholder="Choisir un nom"
          value={groupeName}
          onChange={(e) => setGroupeName(e.target.value)}
          className={cn(groupeNameError && 'border-red-500 focus-visible:ring-red-500')}
        />
        {groupeNameError && <p className="text-sm text-red-600">Le nom du groupe est obligatoire</p>}
      </div>

      <div className="flex flex-col gap-3">
        <h2>Pèlerins sélectionnés</h2>

        <div className="rounded-sm overflow-hidden border border-gray-200">
          <Table>
            <TableHeader>
              <TableHead>Nom</TableHead>
              <TableHead>Catégorie</TableHead>
            </TableHeader>

            <TableBody>
              {currentItems.map((trip) => (
                <TableRow key={trip.id}>
                  <TableCell>{getUserName(trip.pilgrim.first_name, trip.pilgrim.last_name)}</TableCell>
                  <TableCell>
                    <CategoryBadge categoryName={trip.category_name} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            {totalPages > 1 && (
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={2}>
                    <div className="flex items-center justify-between mt-6 text-sm text-gray-600 ">
                      <p className="font-medium">
                        Page {page} sur {totalPages}
                      </p>

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-lg"
                          disabled={page === 1}
                          onClick={() => goToPage(page - 1)}
                        >
                          Précédent
                        </Button>

                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-lg"
                          disabled={page >= totalPages}
                          onClick={() => goToPage(page + 1)}
                        >
                          Suivant
                        </Button>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </div>
      </div>
    </Modal>
  );
};

CreateGroupTripModal.propTypes = {
  isOpened: PropTypes.bool,
  selectedPilgrimIds: PropTypes.shape([]),
  selectedPilgrimsData: PropTypes.shape([]),
  closeModal: PropTypes.func,
};

export default CreateGroupTripModal;
